import React from 'react';
import LoadingIndicator from '../../../../../Components/Loading/LoadingIndicator';
import PickPriceListModal from '../../modals/PickPriceListModal';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DeleteModal from 'Components/Common/DeleteModal';
import { usePriceListData } from './hooks/usePriceListData';
import { usePriceListEdit } from './hooks/usePriceListEdit';
import { useModalManagement } from './hooks/useModalManagement';
import { useRowSelection } from './hooks/useRowSelection';
import { useDeleteOperations } from './hooks/useDeleteOperations';
import { PriceTabToolbar } from './PriceTabToolbar';
import { PriceListTable } from './PriceListTable';

interface PriceTabContainerProps {
  isActive?: boolean;
}

export const PriceTabContainer: React.FC<PriceTabContainerProps> = ({ isActive = false }) => {
  // Redux store state
  const selectedProductRowId = useSelector((state: RootState) => state.products.selectedProduct?.rowId || 0);

  // Data hook
  const {
    priceListData,
    priceListTaxOptions,
    refreshPriceListType,
    isInitialLoading,
    isRefreshing,
    hasData,
    productPriceListError,
    refetchPriceList,
    handleAssignToAllPriceLists,
    resetInitialTimestamps,
    getProductPriceListAuditDetails,
    initialUpdatedTimestamps,
    setInitialUpdatedTimestamps
  } = usePriceListData(selectedProductRowId, isActive);

  // Edit hook
  const {
    updatingItems,
    changedCells,
    hasUnsavedChanges,
    isRefreshingPriceList,
    handleCellChange,
    handleSave,
    handleCancel,
    handleToggleAllowDiscount,
    handleToggleUseVolumePricing,
    handleTaxFieldChange,
    handleRefreshProductPriceList,
    handleRefreshAllPriceLists
  } = usePriceListEdit({
    priceListData,
    selectedProductRowId,
    initialUpdatedTimestamps,
    setInitialUpdatedTimestamps,
    getProductPriceListAuditDetails,
    refetchPriceList,
    resetInitialTimestamps
  });

  // Custom hooks for different concerns
  const modalHook = useModalManagement(refetchPriceList);
  const selectionHook = useRowSelection(priceListData);
  const deleteHook = useDeleteOperations(selectedProductRowId, refetchPriceList);

  // Get selected price list row ID
  const selectedPriceListRowId = selectionHook.getSelectedRow()?.priceListId;

  // Delete all price lists for testing
  const handleDelete = () => {
    deleteHook.handleDelete(priceListData, selectionHook.clearSelection);
  };

  // Handle refresh price list for selected row
  const handleRefreshPriceList = () => {
    if (selectedPriceListRowId) {
      handleRefreshProductPriceList({ 
        productRowId: selectedProductRowId, 
        body: { priceListId: selectedPriceListRowId, type: 'Price' } 
      });
    }
  };

  if (isInitialLoading) {
    return (
      <div className="page-content p-0">
        <LoadingIndicator 
          isLoading={true} 
          title="Loading product price list..." 
          height="400px" 
          className="w-100" 
        />
      </div>
    );
  }

  if (productPriceListError) {
    return (
      <div className="page-content p-0">
        <LoadingIndicator error={`Failed to load product price list: ${productPriceListError}`} height="400px" className="w-100" />
      </div>
    );
  }

  if (!selectedProductRowId) {
    return (
      <div className="page-content p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <div className="text-center text-muted">
            <i className="ri-information-line fs-24 mb-2"></i>
            <p>Please select a product to view its price list.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content p-0">
      <PriceTabToolbar
        selectedProductRowId={selectedProductRowId}
        hasUnsavedChanges={hasUnsavedChanges}
        hasSelectedRow={selectionHook.hasSelectedRow}
        hasData={hasData}
        isRefreshing={isRefreshing}
        isRefreshingPriceList={isRefreshingPriceList}
        selectedPriceListRowId={selectedPriceListRowId}
        refreshPriceListType={refreshPriceListType}
        onNew={modalHook.openModal}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onAssignToAll={handleAssignToAllPriceLists}
        onRefresh={refetchPriceList}
        changedCellsCount={Object.keys(changedCells).length}
        onRefreshProductPriceList={() => handleRefreshProductPriceList({ productRowId: selectedProductRowId, body: { priceListId: 0, type: 'Price' } })}
        onRefreshAllPriceLists={handleRefreshAllPriceLists}
        onRefreshPriceList={handleRefreshPriceList}
      />

      <PriceListTable
        priceListData={priceListData}
        priceListTaxOptions={priceListTaxOptions}
        hasData={hasData}
        updatingItems={updatingItems}
        changedCells={changedCells}
        rowSelection={selectionHook.rowSelection}
        handleCellChange={handleCellChange}
        handleTaxFieldChange={handleTaxFieldChange}
        handleToggleAllowDiscount={handleToggleAllowDiscount}
        handleToggleUseVolumePricing={handleToggleUseVolumePricing}
        onRowSelectionChange={selectionHook.setRowSelection}
        handleRowSelect={selectionHook.handleRowSelect}
      />

      <PickPriceListModal
        isOpen={modalHook.isPickPriceListModalOpen}
        onClose={modalHook.closeModal}
        onSelect={modalHook.handleSelectPriceList}
      />

      {/* Delete Confirmation Modal */}
      {deleteHook.confirmation && (
        <DeleteModal
          show={deleteHook.confirmation.isOpen}
          onDeleteClick={deleteHook.handleConfirm}
          onCloseClick={deleteHook.hideConfirmation}
          title={deleteHook.confirmation.title}
          message={deleteHook.confirmation.message}
          recordName={deleteHook.confirmation.recordName}
          recordId={deleteHook.confirmation.recordId}
          deleteButtonText={deleteHook.confirmation.deleteButtonText}
          isLoading={deleteHook.confirmation.isLoading}
        />
      )}
    </div>
  );
};
